// filepath: apps/web/src/renderers/form/fields/IntegerField.tsx
//
// Integer-only number input. The browser will reject decimals when step=1,
// but we also defend in depth by parsing the value and flooring.

import React from "react";
import type { FieldComponentProps } from "../FieldRegistry";
import { getUiSchemaField } from "../FieldRegistry";
import { useTranslation } from "@/i18n/useTranslation";
import { resolveLocalizedValue } from "@/i18n/localeMap";
import { useIsMobile, MIN_TOUCH_TARGET_PX } from "@/runtime/useIsMobile";

export function IntegerField({
  fieldId,
  value,
  onChange,
  disabled,
  uiSchema,
  fieldError,
}: FieldComponentProps): React.ReactElement {
  const { locale } = useTranslation();
  const isMobile = useIsMobile();
  const uiField = getUiSchemaField(uiSchema, fieldId);
  const labelText = resolveLocalizedValue(uiField?.label, locale, fieldId);
  const helpText = resolveLocalizedValue(uiField?.help, locale, "");
  const helpId = `field-help-${fieldId}`;
  const errorId = fieldError?.id;
  const describedByParts = [helpText ? helpId : null, errorId].filter(
    (part): part is string => typeof part === "string",
  );
  const describedBy =
    describedByParts.length > 0 ? describedByParts.join(" ") : undefined;

  const labelStyle: React.CSSProperties = isMobile
    ? {
        display: "block",
        marginBottom: "4px",
        wordBreak: "break-word",
        overflowWrap: "break-word",
        minHeight: `${MIN_TOUCH_TARGET_PX}px`,
        lineHeight: "1.4",
      }
    : {};

  const inputStyle: React.CSSProperties = isMobile
    ? {
        width: "100%",
        minHeight: `${MIN_TOUCH_TARGET_PX}px`,
        fontSize: "16px",
        padding: "8px 12px",
        boxSizing: "border-box",
      }
    : {};

  const helpStyle: React.CSSProperties = isMobile
    ? {
        fontSize: "14px",
        marginTop: "4px",
        wordBreak: "break-word",
        overflowWrap: "break-word",
      }
    : {};

  return (
    <div data-testid={`field-${fieldId}`} className="field field-integer">
      <label htmlFor={fieldId} style={labelStyle}>
        {labelText}
        {uiField?.required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={fieldId}
        type="number"
        name={fieldId}
        step="1"
        value={value === undefined || value === null ? "" : String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange(null);
            return;
          }
          const num = Number(raw);
          if (Number.isFinite(num)) {
            onChange(Math.trunc(num));
          } else {
            onChange(raw);
          }
        }}
        disabled={disabled}
        aria-required={uiField?.required ? "true" : undefined}
        aria-invalid={fieldError ? "true" : undefined}
        aria-describedby={describedBy}
        data-testid={`input-${fieldId}`}
        style={inputStyle}
      />
      {helpText ? (
        <p id={helpId} className="field-help" style={helpStyle}>
          {helpText}
        </p>
      ) : null}
    </div>
  );
}
